import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User, Mail, Phone, MessageSquare, Loader2 } from "lucide-react";

const contactSchema = z.object({
  name: z.string().trim().min(2, "Name muss mindestens 2 Zeichen lang sein").max(100, "Name zu lang"),
  email: z.string().trim().email("Ungültige E-Mail-Adresse").max(255, "E-Mail zu lang"),
  phone: z.string().trim().min(5, "Telefonnummer zu kurz").max(30, "Telefonnummer zu lang"),
  message: z.string().trim().max(1000, "Nachricht zu lang").optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-consultation-request', {
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          message: data.message || '',
        },
      });

      if (error) throw error;

      toast({
        title: "Erfolgreich gesendet!",
        description: "Wir melden uns innerhalb von 24 Stunden bei Ihnen.",
      });

      reset();
    } catch (error) {
      console.error('Error sending consultation request:', error);
      toast({
        title: "Fehler beim Senden",
        description: "Bitte versuchen Sie es später erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" itemScope itemType="https://schema.org/ContactPage">
      <div className="space-y-2">
        <Label htmlFor="name" className="flex items-center gap-2">
          <User className="w-4 h-4 text-accent" />
          Name *
        </Label>
        <Input
          id="name"
          itemProp="name"
          {...register("name")}
          placeholder="Ihr vollständiger Name"
          className={errors.name ? "border-destructive" : ""}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-accent" />
          E-Mail *
        </Label>
        <Input
          id="email"
          type="email"
          itemProp="email"
          {...register("email")}
          placeholder="ihre.email@beispiel.de"
          className={errors.email ? "border-destructive" : ""}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-accent" />
          Telefon *
        </Label>
        <Input
          id="phone"
          type="tel"
          itemProp="telephone"
          {...register("phone")}
          placeholder="+49 123 456789"
          className={errors.phone ? "border-destructive" : ""}
        />
        {errors.phone && (
          <p className="text-sm text-destructive">{errors.phone.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-accent" />
          Nachricht (optional)
        </Label>
        <Textarea
          id="message"
          {...register("message")}
          placeholder="Teilen Sie uns mit, wie wir Ihnen helfen können..."
          rows={4}
          className={errors.message ? "border-destructive" : ""}
        />
        {errors.message && (
          <p className="text-sm text-destructive">{errors.message.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-accent text-accent-foreground hover:bg-accent/90 transition-all hover:shadow-[0_0_20px_rgba(139,195,74,0.5)]"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Wird gesendet...
          </>
        ) : (
          "Kontaktdaten absenden"
        )}
      </Button>

      <p className="text-xs text-foreground/60 text-center">
        * Pflichtfelder
      </p>
    </form>
  );
};

export default ContactForm;
